import * as React from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useActiveServices, useCreateTransaction, useMerchantMap, useMerchantScope } from '@/hooks';
import { useWebhookConfig } from '@/hooks/use-webhook-config';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { formatPrice } from '@/types';
import type { CreateTransactionRequest, FlowType, OperatorID, Transaction } from '@/types';
import type { TFunction } from 'i18next';

/**
 * Factory function that creates the Zod schema with translated validation messages.
 * This ensures validation messages are resolved at render time, not module load time.
 */
function createTransactionSchema(t: TFunction) {
  return z.object({
    serviceId: z.string().min(1, t('form.validation.serviceRequired')),
    callbackOk: z.string().url(t('form.validation.urlRequired')).startsWith('https://', t('form.validation.urlHttps')),
    callbackError: z.string().url(t('form.validation.urlRequired')).startsWith('https://', t('form.validation.urlHttps')).optional().or(z.literal('')),
    destination: z
      .string()
      .regex(/^[1-9]\d{6,14}$/, t('form.validation.msisdnFormat'))
      .optional()
      .or(z.literal('')),
    flowType: z.enum(['WEB_TAN', 'WAP_CLICK']).optional(),
    operatorId: z.enum(['D1_DE', 'D2_DE', 'O2_DE']).optional(),
    description: z.string().max(32, t('form.validation.maxChars', { max: 32 })).optional(),
    customerReference: z.string().max(100, t('form.validation.maxChars', { max: 100 })).optional(),
  });
}

type TransactionFormData = z.infer<ReturnType<typeof createTransactionSchema>>;

interface TransactionFormProps {
  /** Called when transaction is successfully created */
  onSuccess?: (transaction: Transaction) => void;
  /** Pre-set and lock service selection (used from service detail context) */
  lockedServiceId?: string;
  /** When true, renders without the Card wrapper (for use inside dialogs) */
  embedded?: boolean;
}

/**
 * Transaction trigger form component.
 *
 * Allows users to create a new payment transaction by selecting
 * a service and providing optional parameters like MSISDN.
 */
export function TransactionForm({ onSuccess, lockedServiceId, embedded }: TransactionFormProps) {
  const { t } = useTranslation('transactions');
  const { data: servicesData, isLoading: servicesLoading, error: servicesError } = useActiveServices();
  const { mutate: createTransaction, isPending, error: createError, reset: resetMutation } = useCreateTransaction();

  const [formData, setFormData] = React.useState<TransactionFormData>({
    serviceId: lockedServiceId ?? '',
    callbackOk: '',
    callbackError: '',
    destination: '',
    flowType: undefined,
    operatorId: undefined,
    description: '',
    customerReference: '',
  });
  const [skipLandingPage, setSkipLandingPage] = React.useState<boolean | undefined>(undefined);

  const [errors, setErrors] = React.useState<Partial<Record<keyof TransactionFormData, string>>>({});
  const userEditedCallbacks = React.useRef(false);

  // Merchant-Lookup fuer Webhook-Config Prefill
  const selectedService = servicesData?.data.find((s) => s.serviceId === formData.serviceId);
  const selectedMerchantId = selectedService?.merchantId ?? '';
  const { resolveMerchantName } = useMerchantMap();
  // Spec 0220: Merchant-Name im Webhook-Hint nur fuer Platform-Rollen.
  const { showMerchant } = useMerchantScope();
  const { data: webhookConfig } = useWebhookConfig(selectedMerchantId);

  // Wenn Service sich aendert: Callbacks leeren + manuelle Edit-Markierung zuruecksetzen
  React.useEffect(() => {
    userEditedCallbacks.current = false;
    setFormData((prev) => ({ ...prev, callbackOk: '', callbackError: '' }));
  }, [formData.serviceId]);

  // Callback-URLs aus Webhook-Config vorbefuellen wenn Service gewaehlt wird
  React.useEffect(() => {
    if (webhookConfig?.webhookUrl && !userEditedCallbacks.current && selectedMerchantId) {
      setFormData((prev) => ({
        ...prev,
        callbackOk: webhookConfig.webhookUrl,
        callbackError: webhookConfig.webhookUrl,
      }));
    }
  }, [webhookConfig?.webhookUrl, selectedMerchantId]);

  const handleInputChange = (field: keyof TransactionFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Manuelle Callback-Edits tracken, damit Prefill nicht ueberschreibt
    if (field === 'callbackOk' || field === 'callbackError') {
      userEditedCallbacks.current = true;
    }
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSelectChange = (field: keyof TransactionFormData, value: string | undefined) => {
    setFormData((prev) => ({ ...prev, [field]: value || undefined }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    resetMutation();

    // Validate form data
    const schema = createTransactionSchema(t);
    const result = schema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof TransactionFormData, string>> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof TransactionFormData;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    // Build request
    const request: CreateTransactionRequest = {
      ServiceID: formData.serviceId,
      callback_ok: formData.callbackOk,
    };

    if (formData.callbackError) {
      request.callback_error = formData.callbackError;
    }
    if (formData.destination) {
      request.Destination = formData.destination;
    }
    if (formData.flowType) {
      request.FlowType = formData.flowType as FlowType;
    }
    if (formData.operatorId) {
      request.OperatorID = formData.operatorId as OperatorID;
    }
    if (formData.description) {
      request.Description = formData.description;
    }
    if (formData.customerReference) {
      request.CustomerReference = formData.customerReference;
    }
    if (skipLandingPage !== undefined) {
      request.SkipLandingPage = skipLandingPage;
    }

    createTransaction(request, {
      onSuccess: (transaction) => {
        onSuccess?.(transaction);
      },
    });
  };

  const formContent = (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Service Selection */}
          <div className="space-y-2">
            <Label htmlFor="service">{t('form.label.service')} *</Label>
            {lockedServiceId ? (
              <>
                <Input
                  id="service"
                  value={lockedServiceId}
                  disabled
                  className="font-mono bg-muted"
                />
                {selectedService && (
                  <p className="text-sm text-muted-foreground">
                    {selectedService.description}
                    {selectedService.grossAmount ? ` — ${formatPrice(selectedService.grossAmount)}` : ''}
                  </p>
                )}
              </>
            ) : servicesLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" />
                {t('form.loading')}
              </div>
            ) : servicesError ? (
              <ApiErrorDisplay error={servicesError} />
            ) : (
              <>
                <Select
                  value={formData.serviceId}
                  onValueChange={(value) => handleSelectChange('serviceId', value)}
                >
                  <SelectTrigger id="service" aria-describedby={errors.serviceId ? 'service-error' : undefined}>
                    <SelectValue placeholder={t('form.placeholder.selectService')} />
                  </SelectTrigger>
                  <SelectContent>
                    {servicesData?.data.map((service) => (
                      <SelectItem key={service.serviceId} value={service.serviceId}>
                        {service.description || service.serviceId}
                        {service.grossAmount && ` - ${formatPrice(service.grossAmount)}`}
                        {service.subscriptionInterval && ` / ${t(`services:interval.${service.subscriptionInterval}`)}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.serviceId && (
                  <p id="service-error" className="text-sm text-destructive">
                    {errors.serviceId}
                  </p>
                )}
                {selectedService && (
                  <p className="text-sm text-muted-foreground">
                    ID: {selectedService.serviceId}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Callback URLs */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="callbackOk">{t('form.label.callbackOk')} *</Label>
              <Input
                id="callbackOk"
                type="url"
                placeholder="https://example.com/success"
                value={formData.callbackOk}
                onChange={(e) => handleInputChange('callbackOk', e.target.value)}
                aria-describedby={errors.callbackOk ? 'callbackOk-error' : undefined}
              />
              {errors.callbackOk && (
                <p id="callbackOk-error" className="text-sm text-destructive">
                  {errors.callbackOk}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="callbackError">{t('form.label.callbackError')}</Label>
              <Input
                id="callbackError"
                type="url"
                placeholder="https://example.com/error"
                value={formData.callbackError}
                onChange={(e) => handleInputChange('callbackError', e.target.value)}
                aria-describedby={errors.callbackError ? 'callbackError-error' : undefined}
              />
              {errors.callbackError && (
                <p id="callbackError-error" className="text-sm text-destructive">
                  {errors.callbackError}
                </p>
              )}
            </div>
          </div>
          {/* Webhook-Prefill Hinweis */}
          {selectedMerchantId && (
            <p className="text-xs text-muted-foreground -mt-2">
              {webhookConfig?.webhookUrl
                ? showMerchant
                  ? t('form.webhookPrefillMerchant', { merchant: resolveMerchantName(selectedMerchantId) })
                  : t('form.webhookPrefill')
                : showMerchant
                  ? t('form.webhookNoneMerchant', { merchant: resolveMerchantName(selectedMerchantId) })
                  : t('form.webhookNone')}
            </p>
          )}

          {/* Optional Fields */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="destination">{t('form.label.destination')}</Label>
              <Input
                id="destination"
                type="tel"
                placeholder="4915123456789"
                value={formData.destination}
                onChange={(e) => handleInputChange('destination', e.target.value)}
                aria-describedby={errors.destination ? 'destination-error' : 'destination-hint'}
              />
              {errors.destination ? (
                <p id="destination-error" className="text-sm text-destructive">
                  {errors.destination}
                </p>
              ) : (
                <p id="destination-hint" className="text-sm text-muted-foreground">
                  {t('form.hint.destination')}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="flowType">{t('form.label.flowType')}</Label>
              <Select
                value={formData.flowType || ''}
                onValueChange={(value) => handleSelectChange('flowType', value || undefined)}
              >
                <SelectTrigger id="flowType">
                  <SelectValue placeholder={t('form.placeholder.autoDetect')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="WEB_TAN">WEB_TAN (SMS PIN)</SelectItem>
                  <SelectItem value="WAP_CLICK">WAP_CLICK (Carrier Consent)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="operatorId">{t('form.label.operatorId')}</Label>
              <Select
                value={formData.operatorId || ''}
                onValueChange={(value) => handleSelectChange('operatorId', value || undefined)}
              >
                <SelectTrigger id="operatorId">
                  <SelectValue placeholder={t('form.placeholder.autoDetect')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="D1_DE">Telekom (D1)</SelectItem>
                  <SelectItem value="D2_DE">Vodafone (D2)</SelectItem>
                  <SelectItem value="O2_DE">O2/Telefónica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerReference">{t('form.label.customerReference')}</Label>
              <Input
                id="customerReference"
                type="text"
                placeholder="ORDER_12345"
                maxLength={100}
                value={formData.customerReference}
                onChange={(e) => handleInputChange('customerReference', e.target.value)}
                aria-describedby={errors.customerReference ? 'customerReference-error' : undefined}
              />
              {errors.customerReference && (
                <p id="customerReference-error" className="text-sm text-destructive">
                  {errors.customerReference}
                </p>
              )}
            </div>
          </div>

          {/* Landing-Page Override (Contract v2.0) */}
          <div className="space-y-2">
            <Label className="text-sm">{t('form.label.skipLandingPage')}</Label>
            <Select
              value={skipLandingPage === undefined ? '' : skipLandingPage ? 'true' : 'false'}
              onValueChange={(value) => setSkipLandingPage(value === '' ? undefined : value === 'true')}
            >
              <SelectTrigger id="skipLandingPage">
                <SelectValue placeholder={t('form.placeholder.serviceDefault')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="false">{t('form.landingShowFacade')}</SelectItem>
                <SelectItem value="true">{t('form.landingSkip')}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t('form.landingOverrideHint')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-muted-foreground">
              {t('form.label.description')}
              <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400">{t('form.hint.deprecated')}</span>
            </Label>
            <Input
              id="description"
              type="text"
              placeholder="Premium Subscription"
              maxLength={32}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              aria-describedby={errors.description ? 'description-error' : 'description-deprecated-hint'}
              className="border-dashed"
            />
            {errors.description ? (
              <p id="description-error" className="text-sm text-destructive">
                {errors.description}
              </p>
            ) : (
              <p id="description-deprecated-hint" className="text-xs text-amber-600 dark:text-amber-400">
                {t('form.hint.descriptionDeprecated')}
              </p>
            )}
          </div>

          {/* API Error */}
          {createError && (
            <ApiErrorDisplay error={createError} />
          )}

          {/* Submit Button */}
          <Button type="submit" disabled={isPending || servicesLoading} loading={isPending}>
            {t('form.submit')}
          </Button>
        </form>
  );

  if (embedded) {
    return formContent;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('form.title')}</CardTitle>
        <CardDescription>
          {t('form.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {formContent}
      </CardContent>
    </Card>
  );
}
